class GeoLocationService {
  // Testing mode - set this to true to enable location override
  static TESTING_MODE = true; // Set to false in production

  static async getUserLocation() {
    try {
      // Check for manual location override in testing mode
      if (this.TESTING_MODE) {
        const manualLocation = this.getManualLocationOverride();
        if (manualLocation) {
          console.log('🧪 TESTING MODE: Using manual location override:', manualLocation);
          return manualLocation;
        }
      }

      // Try multiple IP geolocation services for reliability
      const services = [
        'https://ipapi.co/json/',
        'https://ip-api.com/json/',
        'https://ipinfo.io/json'
      ];

      for (const service of services) {
        try {
          const response = await fetch(service);
          if (response.ok) {
            const data = await response.json();
            return this.normalizeLocationData(data, service);
          }
        } catch (error) {
          console.warn(`Geolocation service ${service} failed:`, error);
          continue;
        }
      }

      // Fallback to browser geolocation if IP services fail
      return await this.getBrowserLocation();
    } catch (error) {
      console.error('All geolocation services failed:', error);
      return this.getDefaultLocation();
    }
  }

  static getManualLocationOverride() {
    // Check localStorage for manual override
    const override = localStorage.getItem('geoLocationOverride');
    if (override) {
      try {
        const parsed = JSON.parse(override);
        this.setComplianceFlags(parsed);
        return parsed;
      } catch (error) {
        console.error('Invalid location override format:', error);
        localStorage.removeItem('geoLocationOverride');
      }
    }
    return null;
  }

  static setLocationOverride(countryCode, region = '', city = 'Test City') {
    if (!this.TESTING_MODE) {
      console.warn('Location override only available in testing mode');
      return false;
    }

    const testLocation = {
      country: this.getCountryName(countryCode),
      countryCode: countryCode,
      region: region,
      city: city,
      ip: '127.0.0.1',
      isEU: false,
      isChina: false,
      isHighPrivacyRegion: false,
      requiresCookieConsent: false,
      requiresDataProtection: false,
      isTestOverride: true
    };

    this.setComplianceFlags(testLocation);
    localStorage.setItem('geoLocationOverride', JSON.stringify(testLocation));
    console.log('🧪 Location override set:', testLocation);
    return true;
  }

  static clearLocationOverride() {
    localStorage.removeItem('geoLocationOverride');
    console.log('🧪 Location override cleared');
  }

  static getCountryName(countryCode) {
    const countries = {
      'CN': 'China',
      'US': 'United States',
      'CA': 'Canada',
      'GB': 'United Kingdom',
      'DE': 'Germany',
      'FR': 'France',
      'IT': 'Italy',
      'ES': 'Spain',
      'NL': 'Netherlands',
      'BE': 'Belgium',
      'AU': 'Australia',
      'JP': 'Japan',
      'KR': 'South Korea',
      'IN': 'India',
      'BR': 'Brazil'
    };
    return countries[countryCode] || 'Unknown';
  }

  // Pre-configured test scenarios
  static getTestScenarios() {
    return {
      china: {
        name: '🇨🇳 China (Blocked)',
        config: { countryCode: 'CN', region: 'Beijing', city: 'Beijing' }
      },
      eu_germany: {
        name: '🇩🇪 Germany (EU/GDPR)',
        config: { countryCode: 'DE', region: 'Bavaria', city: 'Munich' }
      },
      eu_france: {
        name: '🇫🇷 France (EU/GDPR)',
        config: { countryCode: 'FR', region: 'Île-de-France', city: 'Paris' }
      },
      uk: {
        name: '🇬🇧 UK (Post-Brexit)',
        config: { countryCode: 'GB', region: 'England', city: 'London' }
      },
      california: {
        name: '🇺🇸 California (CPRA)',
        config: { countryCode: 'US', region: 'California', city: 'San Francisco' }
      },
      quebec: {
        name: '🇨🇦 Quebec (Law 25)',
        config: { countryCode: 'CA', region: 'Quebec', city: 'Montreal' }
      },
      australia: {
        name: '🇦🇺 Australia',
        config: { countryCode: 'AU', region: 'New South Wales', city: 'Sydney' }
      },
      normal_us: {
        name: '🇺🇸 US (Normal)',
        config: { countryCode: 'US', region: 'Texas', city: 'Dallas' }
      }
    };
  }

  static normalizeLocationData(data, service) {
    let normalized = {
      country: '',
      countryCode: '',
      region: '',
      city: '',
      ip: '',
      isEU: false,
      isChina: false,
      isHighPrivacyRegion: false,
      requiresCookieConsent: false,
      requiresDataProtection: false
    };

    // Normalize data based on service
    if (service.includes('ipapi.co')) {
      normalized.country = data.country_name;
      normalized.countryCode = data.country;
      normalized.region = data.region;
      normalized.city = data.city;
      normalized.ip = data.ip;
    } else if (service.includes('ip-api.com')) {
      normalized.country = data.country;
      normalized.countryCode = data.countryCode;
      normalized.region = data.regionName;
      normalized.city = data.city;
      normalized.ip = data.query;
    } else if (service.includes('ipinfo.io')) {
      normalized.country = data.country;
      normalized.countryCode = data.country;
      normalized.region = data.region;
      normalized.city = data.city;
      normalized.ip = data.ip;
    }

    // Set compliance flags
    this.setComplianceFlags(normalized);

    return normalized;
  }

  static setComplianceFlags(location) {
    const euCountries = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];
    const highPrivacyRegions = [...euCountries, 'CA', 'GB', 'AU', 'NZ'];
    const cookieConsentRequired = [...euCountries, 'GB'];
    
    location.isEU = euCountries.includes(location.countryCode);
    location.isChina = location.countryCode === 'CN';
    location.isHighPrivacyRegion = highPrivacyRegions.includes(location.countryCode);
    location.requiresCookieConsent = cookieConsentRequired.includes(location.countryCode);
    location.requiresDataProtection = location.isHighPrivacyRegion;

    // Special handling for US states (California CPRA)
    if (location.countryCode === 'US' && location.region && location.region.toLowerCase().includes('california')) {
      location.isHighPrivacyRegion = true;
      location.requiresDataProtection = true;
    }

    // Special handling for Quebec
    if (location.countryCode === 'CA' && location.region && location.region.toLowerCase().includes('quebec')) {
      location.requiresDataProtection = true;
    }
  }

  static async getBrowserLocation() {
    return new Promise((resolve) => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              country: 'Unknown',
              countryCode: '',
              region: 'Unknown',
              city: 'Unknown',
              ip: 'Unknown',
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              isEU: false,
              isChina: false,
              isHighPrivacyRegion: false,
              requiresCookieConsent: false,
              requiresDataProtection: false
            });
          },
          () => resolve(this.getDefaultLocation())
        );
      } else {
        resolve(this.getDefaultLocation());
      }
    });
  }

  static getDefaultLocation() {
    return {
      country: 'Unknown',
      countryCode: '',
      region: 'Unknown', 
      city: 'Unknown',
      ip: 'Unknown',
      isEU: false,
      isChina: false,
      isHighPrivacyRegion: false,
      requiresCookieConsent: false,
      requiresDataProtection: false
    };
  }

  static isChinaBlocked(location) {
    return location.isChina;
  }

  static getRegionalCompliance(location) {
    return {
      showCookieConsent: location.requiresCookieConsent,
      showDataProtectionNotices: location.requiresDataProtection,
      showAIDisclaimers: location.isHighPrivacyRegion,
      requiresOptOut: location.isHighPrivacyRegion,
      blockedRegion: location.isChina
    };
  }

  static getRegionalLegalText(location) {
    const texts = {
      EU: {
        cookieNotice: 'We use cookies to improve your experience. By continuing, you consent to our use of cookies.',
        dataNotice: 'Your data is processed in accordance with GDPR. You have rights to access, modify, and delete your data.',
        aiDisclaimer: 'AI-generated content is for guidance only. Please review and verify before use.'
      },
      UK: {
        cookieNotice: 'We use cookies to enhance your experience. Continuing indicates your consent.',
        dataNotice: 'Data processing complies with UK GDPR. Contact us for data requests.',
        aiDisclaimer: 'AI outputs are suggestions only. Professional verification recommended.'
      },
      CA: {
        dataNotice: 'Data handling follows Canadian privacy laws including Quebec Law 25.',
        aiDisclaimer: 'AI-generated content requires professional review before implementation.'
      },
      US_CA: {
        dataNotice: 'California residents have CPRA rights regarding personal data.',
        aiDisclaimer: 'AI content is not professional advice. Independent verification required.'
      },
      DEFAULT: {
        aiDisclaimer: 'AI-generated content is for informational purposes only. Please review carefully.'
      }
    };

    if (location.isEU) return texts.EU;
    if (location.countryCode === 'GB') return texts.UK;
    if (location.countryCode === 'CA') return texts.CA;
    if (location.countryCode === 'US' && location.region?.toLowerCase().includes('california')) return texts.US_CA;
    return texts.DEFAULT;
  }
}

// Development helpers - expose to window for console testing
if (GeoLocationService.TESTING_MODE && typeof window !== 'undefined') {
  // Check if user is test user
  const isTestUser = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.email === 'test@trakvu.com';
    } catch (error) {
      return false;
    }
  };

  // Only expose helpers for test user
  if (isTestUser()) {
    window.geoTest = {
      // Quick test functions
      testChina: () => GeoLocationService.setLocationOverride('CN', 'Beijing', 'Beijing'),
      testGermany: () => GeoLocationService.setLocationOverride('DE', 'Bavaria', 'Munich'),
      testFrance: () => GeoLocationService.setLocationOverride('FR', 'Île-de-France', 'Paris'),
      testUK: () => GeoLocationService.setLocationOverride('GB', 'England', 'London'),
      testCalifornia: () => GeoLocationService.setLocationOverride('US', 'California', 'San Francisco'),
      testQuebec: () => GeoLocationService.setLocationOverride('CA', 'Quebec', 'Montreal'),
      testNormalUS: () => GeoLocationService.setLocationOverride('US', 'Texas', 'Dallas'),
      clear: () => GeoLocationService.clearLocationOverride(),
      reload: () => window.location.reload(),
      
      // Quick test and reload
      china: () => { window.geoTest.testChina(); window.location.reload(); },
      germany: () => { window.geoTest.testGermany(); window.location.reload(); },
      france: () => { window.geoTest.testFrance(); window.location.reload(); },
      uk: () => { window.geoTest.testUK(); window.location.reload(); },
      california: () => { window.geoTest.testCalifornia(); window.location.reload(); },
      quebec: () => { window.geoTest.testQuebec(); window.location.reload(); },
      reset: () => { window.geoTest.clear(); window.location.reload(); },
      
      // Helper info
      help: () => {
        console.log(`
🧪 GEOLOCATION TESTING HELPERS (test@trakvu.com only)

Quick Test Commands:
- geoTest.china()       -> Test China (should be blocked)
- geoTest.germany()     -> Test Germany (EU/GDPR)
- geoTest.france()      -> Test France (EU/GDPR)
- geoTest.uk()          -> Test UK (Post-Brexit)
- geoTest.california()  -> Test California (CPRA)
- geoTest.quebec()      -> Test Quebec (Law 25)
- geoTest.reset()       -> Reset to real location

Manual Commands:
- geoTest.testChina()   -> Set location (no reload)
- geoTest.clear()       -> Clear override (no reload)
- geoTest.reload()      -> Reload page

Current Location:
${JSON.stringify(JSON.parse(localStorage.getItem('userLocation') || '{}'), null, 2)}

Current Compliance:
${JSON.stringify(JSON.parse(localStorage.getItem('compliance') || '{}'), null, 2)}
        `);
      }
    };

    console.log('🧪 Geolocation testing enabled for test@trakvu.com! Type "geoTest.help()" for commands.');
  } else {
    console.log('🌍 Geolocation service loaded (testing restricted to test users).');
  }
}

export default GeoLocationService; 